import { FC, useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '../../../store/slice';

// PACKAGES
import ScrollContainer from 'react-indiana-drag-scroll';
import Moment from 'moment';
import { Spinner } from 'react-bootstrap';

// COMPONENTS
import StatusCell from './StatusCell';
import ScrollButtons from './ScrollButtons';
import SortButton from './SortButton';
import PaginationCustom from '../../Pagination/Pagination';

// UTILS
import { guid } from '../../../libs/utils';
import { ToastMessagesSlice } from '../../../store/slice/ToastMessages';
import SvgMask from '../../_Helpers/SvgMask';
import DelayedFadeIn from '../../_Helpers/DelayedFadeIn';

// TYPES
import apibridge, { GetAssessmentOverviewTypes } from '../../../apibridge';
import { AssessmentSummaryGraphemePhonemeGraphemePhonemeInfo } from '../../../api/models';

interface GraphemePhonemeTableProps {
	assessmentGraphemePhonemeParams: GetAssessmentOverviewTypes['GraphemePhonemeType'];
	setAssessmentGraphemePhonemeParams: React.Dispatch<
		React.SetStateAction<GetAssessmentOverviewTypes['GraphemePhonemeType']>
	>;
	onStudentClick: (classId: string, studentId: string) => Promise<void>;
}

const GraphemePhonemeTable: FC<GraphemePhonemeTableProps> = ({
	assessmentGraphemePhonemeParams,
	setAssessmentGraphemePhonemeParams,
	onStudentClick
}) => {
	const dispatch = useAppDispatch();

	const [isLoading, setIsLoading] = useState(true);
	const [isReloading, setIsReloading] = useState(false);
	const [graphemePhonemeData, setGraphemePhonemeData] = useState<AssessmentSummaryGraphemePhonemeGraphemePhonemeInfo>();

	const {
		currentPage = 0,
		itemsPerPage = 0,
		stages = [],
		students = [],
		totalItems = 0,
		totalPages = 0
	} = graphemePhonemeData || {};

	// Scroll Logic
	const [scrollPosition, setScrollPosition] = useState(0);
	const [scrollWidth, setScrollWidth] = useState(0);

	const scrollContainerRef = useRef<HTMLDivElement>(null);

	const getGraphemePhonemeData = async () => {
		setIsReloading(true);

		const response = await apibridge.getAssessmentSummaryGraphemePhoneme(assessmentGraphemePhonemeParams);
		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				setGraphemePhonemeData(response.data.result);
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Grapheme-phoneme overview error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}

		setIsLoading(false);
		setIsReloading(false);
	};

	useEffect(() => {
		if (!isLoading && scrollContainerRef.current) {
			const containerWidth = scrollContainerRef.current.clientWidth;
			const contentWidth = scrollContainerRef.current.scrollWidth;
			const maxScroll = contentWidth - containerWidth;
			setScrollWidth(maxScroll);
		}
	}, [isLoading]);

	useEffect(() => {
		getGraphemePhonemeData();
	}, [assessmentGraphemePhonemeParams]); // eslint-disable-line react-hooks/exhaustive-deps

	// Reset page to 1 when params change, otherwise pagination will break
	useEffect(() => {
		if ((assessmentGraphemePhonemeParams.pageNumber || 1) !== 1) {
			setAssessmentGraphemePhonemeParams({ ...assessmentGraphemePhonemeParams, pageNumber: 1 });
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		assessmentGraphemePhonemeParams._class,
		assessmentGraphemePhonemeParams.assessmentPeriodId,
		assessmentGraphemePhonemeParams.benchmark
	]);

	return (
		<div className="assessment-general-table">
			{isLoading ? (
				<div className="d-flex align-items-center justify-content-center position-relative w-100">
					<DelayedFadeIn>
						<Spinner animation="border" role="status">
							<span className="visually-hidden">Loading...</span>
						</Spinner>
					</DelayedFadeIn>
				</div>
			) : (
				<>
					<div className={`table-container text-shades-800 ${isReloading ? 'opacity-75' : ''}`}>
						{/* STUDENT DETAILS COLUMN */}
						<div className="table-col-students d-flex flex-column">
							{/* Student Name List Header */}
							<div className="student-row-heading border-bottom-0"></div>

							<div className="student-row-heading">
								<div className="student-name-cell d-flex align-items-center justify-content-between gap-3">
									<span className="body-tiny">
										<strong>Student name</strong>
									</span>

									<SortButton
										type="Student"
										params={assessmentGraphemePhonemeParams}
										setParams={setAssessmentGraphemePhonemeParams}
										disabled={isReloading}
									/>
								</div>

								<div className="last-recorded-cell">
									<span className="body-tiny">
										<strong>Last recorded</strong>
									</span>

									<SortButton
										type="LastRecorded"
										params={assessmentGraphemePhonemeParams}
										setParams={setAssessmentGraphemePhonemeParams}
										disabled={isReloading}
									/>
								</div>
							</div>

							{/* Student name List */}
							{students.length > 0 ? (
								<div className="student-buttons-wrapper">
									{students.map((student, studentIndex) => {
										const { className, firstName, graphemePhonemes, id, lastName, lastRecorded } = student;
										return (
											<button
												className={`btn btn-student student-row d-flex justify-content-between ${
													studentIndex % 2 === 0 ? 'bg-white' : ''
												}`}
												key={studentIndex}
												onClick={() => onStudentClick(student.classId || '', student.id || '')}
											>
												<div className="student-name-cell d-flex flex-column justify-content-center">
													<span className="student-name body-small">
														<strong>
															{firstName} {lastName}
														</strong>
													</span>

													<span className="body-mini fw-semibold opacity-50">{className}</span>
												</div>

												<div className="last-recorded-cell">
													<span className="body-tiny fw-semibold">{Moment(lastRecorded).format('DD/MM/YY')}</span>

													<div className="edit-btn">
														<SvgMask path="/svg/edit.svg" width={24} height={24} />
													</div>
												</div>
											</button>
										);
									})}
								</div>
							) : (
								<div className="student-row d-flex justify-content-between">
									<div className="student-name-cell d-flex flex-column justify-content-center">
										<p className="my-3">No results</p>
									</div>
								</div>
							)}
						</div>

						{/* SCROLL CONTAINER */}
						<div className="table-col-content d-flex scroll-btn-anchor">
							<ScrollButtons
								scrollPosition={scrollPosition}
								setScrollPosition={setScrollPosition}
								scrollWidth={scrollWidth}
								scrollContainerRef={scrollContainerRef}
							/>
							<ScrollContainer
								className="stages-scroll-container"
								hideScrollbars={true}
								vertical={false}
								innerRef={scrollContainerRef}
								onScroll={() => {
									if (scrollContainerRef.current) {
										setScrollPosition(scrollContainerRef.current.scrollLeft);
									}
								}}
							>
								{/* Header */}
								<div className="d-flex">
									{stages?.length > 0 &&
										stages.map((stage, stageIndex) => {
											const { id, name = '', graphemes = [] } = stage;
											const stageClassName = name.replace('.', '-');

											return (
												<div key={stageIndex} className="stage-col">
													{/* Stage Name */}
													<div className={`stage-name stage-${stageClassName}`}>Stage {name}</div>

													{/* Stage Progress Name */}
													<div className="d-flex">
														{graphemes.length > 0 &&
															graphemes.map((grapheme, graphemeIndex) => {
																return (
																	<div className={`stage-progress small stage-${stageClassName}`} key={graphemeIndex}>
																		<span className="stage-progress-name">{grapheme.text}</span>

																		<SortButton
																			type="Letter"
																			params={assessmentGraphemePhonemeParams}
																			setParams={setAssessmentGraphemePhonemeParams}
																			stageId={id}
																			disabled={isReloading}
																			sortLetter={grapheme.text}
																		/>
																	</div>
																);
															})}
													</div>
												</div>
											);
										})}
								</div>

								{/* Student Cells */}
								{students?.length > 0 && (
									<div className="table-row-wrapper">
										{students.map((student, studentIndex) => {
											return (
												<div key={studentIndex} className="table-row d-flex">
													{stages.map((stage) => {
														const { id, name = '', graphemes = [] } = stage;

														return graphemes?.map((grapheme, graphemeIndex) => {
															const studentGraphemeData = student.graphemePhonemes?.find(
																(graphemePhoneme) =>
																	graphemePhoneme.stageId === id && graphemePhoneme.text === grapheme.text
															);
															return (
																<StatusCell
																	key={graphemeIndex}
																	className={`${studentIndex % 2 === 0 ? 'alternate-bg-white' : ''}`}
																	width="small"
																	type="binary-result"
																	isCorrect={
																		studentGraphemeData?.status === undefined ||
																		studentGraphemeData?.status === 'Pending'
																			? undefined
																			: studentGraphemeData?.status === 'Successful'
																	}
																/>
															);
														});
													})}
												</div>
											);
										})}
									</div>
								)}
							</ScrollContainer>
						</div>
					</div>

					{totalPages > 1 && (
						<div className="mt-5 d-flex justify-content-between align-items-center">
							<PaginationCustom
								pageNumber={assessmentGraphemePhonemeParams.pageNumber || 1}
								totalPages={totalPages}
								pageSwitchCallback={(newPageNumber) => {
									setAssessmentGraphemePhonemeParams({
										...assessmentGraphemePhonemeParams,
										pageNumber: newPageNumber
									});
								}}
							/>

							<div className="body-small">
								<strong>
									Showing {itemsPerPage * (currentPage - 1) + 1} -{' '}
									{currentPage < totalPages ? itemsPerPage * currentPage : totalItems} of {totalItems}{' '}
									{totalItems === 1 ? 'student' : 'students'}
								</strong>
							</div>
						</div>
					)}
				</>
			)}
		</div>
	);
};

export default GraphemePhonemeTable;
